import { useEffect, useState } from "react";
import { Instagram, Youtube, Facebook, Search, X } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import HeaderStyle from "../assets/stylesheets/Header.module.css";
import { Logo } from "../assets/logo/Logo";
import { useMapView } from "../hooks/MapViewContext";
import SearchBar from "../components/common/SearchBar";
import { SearchLogo}  from '../assets/logo/Search';

// TikTok Icon
const TikTok = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.6 5.82C15.9 5.06 15.5 4.08 15.5 3H12.4V16.94C12.4 18.6797 11.0797 20 9.34 20C7.6003 20 6.28 18.6797 6.28 16.94C6.28 15.2003 7.6003 13.88 9.34 13.88C9.89 13.88 10.41 14.04 10.86 14.3V11.03C10.36 10.92 9.84 10.87 9.32 10.87C6.04 10.87 3.38 13.54 3.38 16.81C3.38 20.08 6.04 22.75 9.32 22.75C12.6 22.75 15.26 20.08 15.26 16.81V9.66C16.45 10.56 17.91 11.09 19.46 11.09V8C19.46 8 17.75 8.11 16.6 5.82Z"
      fill="currentColor"
    />
  </svg>
);

export default function ListHeader({
  categoryStyle = false,
  searchStyle = false,
  showMapToggle = true,
  hideMobileNav = false
  }){
    
  const { showMap } = useMapView();

  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          setScrollY(currentScrollY);
          setIsScrolled(currentScrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const handleMobileSearchOpen = () => {
    setShowMobileSearch(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    // Trigger immediate dropdown display and focus
    setTimeout(() => {
      const searchInput = document.querySelector('.mobile-search-container input');
      if (searchInput) {
        // Trigger a focus event to ensure dropdown shows
        searchInput.focus();
        // Dispatch a custom event to force dropdown visibility
        searchInput.dispatchEvent(new Event('focus', { bubbles: true }));
      }
    }, 150);
  };

  const handleMobileSearchClose = () => {
    setShowMobileSearch(false);
    // Restore body scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  };

  // Close modal when switching to desktop
  useEffect(() => {
    if (!isMobile && showMobileSearch) {
      handleMobileSearchClose();
    }
  }, [isMobile, showMobileSearch]);

  return (
    <>
      <header
        className={`${HeaderStyle["header"]} ${HeaderStyle["listHeader"]} ${
          isScrolled ? HeaderStyle["scrolled"] : ""
        } ${categoryStyle ? HeaderStyle["category-mode"] : ""} ${
          searchStyle ? HeaderStyle["search-mode"] : ""
        }`}
      >
        <div className={HeaderStyle["header-container"]}>
          <div className={HeaderStyle["header-top"]}>
            {/* Desktop version - Logo, Search, Social */}
            {!isMobile && (
              <>
                <div className={HeaderStyle["logo-container"]}>
                  <a href="/" className={HeaderStyle["logo-link"]}>
                    <Logo
                      className={HeaderStyle["logo"]}
                      aria-label="FarmCiti Logo"
                    />
                  </a>
                </div>

                <div className={HeaderStyle["header-search-container"]}>
                  <SearchBar
                    placeholder="Search farms"
                    className={HeaderStyle["header-search-bar"]}
                  />
                </div>

                <div className={HeaderStyle["social-links"]}>
                  <a href="https://www.instagram.com/farmciti.ca" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <Instagram size={20} />
                  </a>
                  <a href="https://www.tiktok.com/@farmciti_canada" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                    <TikTok size={20} />
                  </a>
                  <a href="https://www.youtube.com/@FarmCitiCanada" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                    <Youtube size={20} />
                  </a>
                  <a href="https://www.facebook.com/people/FarmCiti/61573789681716/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <Facebook size={20} />
                  </a>
                </div>
              </>
            )}

            {/* Mobile version - Search Button or Logo only */}
            {isMobile && (
              <>
                <div className={HeaderStyle["logo-container"]}>
                  <a href="/" className={HeaderStyle["logo-link"]}>
                    <Logo
                      className={HeaderStyle["logo"]}
                      aria-label="FarmCiti Logo"
                    />
                  </a>
                </div>
                {!hideMobileNav && (
                  <button 
                    className={HeaderStyle["mobile-search-logo"]}
                    onClick={handleMobileSearchOpen}
                    >
                    <SearchLogo/>
                  </button>
                )}
              </>
            )}

          </div>
        </div>
      </header>

      {/* Mobile Search Modal */}
      { isMobile && showMobileSearch && (
        <div className={HeaderStyle["mobile-search-modal"]}>
            <div className={HeaderStyle["mobile-search-container"]}>
              <SearchBar
                placeholder="Search for farms"
                className={HeaderStyle["mobile-search-bar"]}
                onMobileSearchClose={handleMobileSearchClose}
              />
            </div>
        </div>
      )}

      {/* mobile navigation */}
      {!hideMobileNav && (
        <div className={`${HeaderStyle["mobile-header-container"]} ${
          showMapToggle && showMap ? HeaderStyle["hidden"] : ""
        }`}>
          <nav className={HeaderStyle["mobile-nav"]}>
            <Link 
              to="/search" 
              className={`${HeaderStyle["mobile-nav-item"]} ${
                searchStyle ? HeaderStyle["active"] : ""
              }`}
            >
              <img
                src="/assets-temp/img/iconamoon_search.png"
                alt="Search"
                className={HeaderStyle["nav-icon"]}
              />
            </Link>

            <Link
              to="/"
              className={`${HeaderStyle["mobile-nav-item"]} ${
                !searchStyle && !categoryStyle ? HeaderStyle["active"] : ""
              }`}
            >
              <img
                src="/assets-temp/img/logo-icon.png"
                alt="Home"
                className={HeaderStyle["nav-icon"]}
              />
            </Link>

            <Link to="/" className={HeaderStyle["mobile-nav-item"]}>
              <img
                src="/assets-temp/img/ic_round-menu.png"
                alt="Categories"
                className={HeaderStyle["nav-icon"]}
              />
            </Link>
          </nav>
        </div>
      )}
    </>
  );
}